import React from 'react'
import styled from 'styled-components'
import NoImg from '../icons/NoImg'
import Link from 'next/link'
import CategoriesDetails from '../CategoriesDetails'
import Text from '../Text'

import { SCREEN_SIZE } from '../../lib/Media'
import ProgressiveImage from '../ProgressiveImage/ProgressiveImage'
import { EventTrackingConsumer } from '../EventTrackingProvider/EventTrackingProvider'
import { GTM_ENV_VAR as GEV } from '../../lib/eventTrackerFunction'
import { AMP_EVENT_NAME, getPageTypeByUrl } from '../../lib/amplitude'
import { getCtaLocationMeta } from '../../lib/utils'
import { trackCategoryClickedEvent } from '../../lib/analytics/events/category/CategoryClickedEvent'
import { CheckoutAddressConsumer } from '../CheckoutAddressProvider/CheckoutAddressProvider'

const CategoryContainer = styled.ul`
  display: flex;
  flex-wrap: wrap;

  ${SCREEN_SIZE.From.Desktop} {
    display: flex;
    flex-wrap: wrap;
    margin: 0 auto;
  }

  ${SCREEN_SIZE.From.Hd} {
    width: 100%;
  }
`

const StyledCategory = styled.li`
  background-color: #ffffff;
  border: 1px solid ${({ selected }) => (selected ? '#1557bf' : '#eaeaea')};
  cursor: pointer;
  border-radius: 4px;
  overflow: hidden;
  text-transform: capitalize;
  height: 9.375rem;
  width: calc((100% - 1rem) / 3);
  order: ${props => props.order + 1};
  margin-bottom: 0.5rem;
  margin-right: 0.5rem;

  :nth-child(6n + 5) {
    margin-right: 0;
  }

  ${SCREEN_SIZE.Only.Tablet} {
    width: calc((100% - 2.5rem) / 6);
    margin-right: 0.5rem;
    margin-bottom: 0.5rem;

    :nth-child(6n + 5) {
      margin-right: 0.5rem;
    }

    :nth-child(12n + 11) {
      margin-right: 0;
    }
  }

  ${SCREEN_SIZE.From.Desktop} {
    height: 19.25rem;
    border-radius: 4px;
    padding: 1rem 0.5rem 0.5rem 0.5rem;
    border: 1px solid #eaeaea;
    margin: 0.5rem 0;
    flex-basis: calc((100% - 7rem) / 3);

    /* stylelint-disable property-no-vendor-prefix */
    -webkit-flex-basis: calc((100% - 2rem) / 3);
    -moz-flex-basis: calc((100% - 2rem) / 3);

    :nth-child(4n + 1) {
      margin-right: 0;
    }

    :nth-child(1n + 1) {
      margin-right: 1rem;
    }

    :nth-child(6n + 11) {
      margin-right: 0;
    }

    :nth-child(5) {
      margin-right: 0;
    }
  }
`
const ImageHeading = styled.div`
  display: flex;
  flex-direction: column;

  ${SCREEN_SIZE.From.Desktop} {
    flex-direction: row;
    align-items: center;
    padding-bottom: 1rem;
    padding-left: 1rem;
  }
`

const Heading = styled.div`
  display: flex;
  align-items: center;
  padding: 0.375rem 0.5rem;
  min-height: 3.6rem;
  overflow: hidden;

  ${SCREEN_SIZE.From.Desktop} {
    flex-flow: column;
    justify-content: center;
    align-items: flex-start;
    padding: 0.75rem;
  }
`

const NoImage = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 5.75rem;
  padding: 0.375rem 0.75rem;
  margin-top: 0.25rem;
  overflow: hidden;
  position: relative;
  ${SCREEN_SIZE.From.Desktop} {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 4.5rem;
    height: 4.5rem;
    padding: 0;
    background-image: none;
  }
`

const CategoryHeading = styled(Text)`
  overflow: hidden;
  min-width: 4.5rem;

  ${SCREEN_SIZE.From.Desktop} {
    font-size: 1.125rem;
    font-weight: bold;
    line-height: 1.33;
    color: #333333;
    margin-bottom: 0.75rem;
  }
`
const CategorySubHeading = styled(CategoryHeading)`
  display: none;

  ${SCREEN_SIZE.From.Desktop} {
    display: inline-block;
    font-size: 0.875rem;
    line-height: 1.43;
    color: #1557bf;
    margin-bottom: 0;
  }
`

const SubCategory = styled.ul`
  display: none;
  text-transform: capitalize;

  ${SCREEN_SIZE.From.Desktop} {
    display: block;
    list-style-type: none;
    padding: 1rem 0.5rem 0 0.5rem;
    margin: 0 0.5rem;
    height: 12rem;
    border-top: 1px solid #eaeaea;
    column-count: 2;
    column-fill: auto;
    overflow: hidden;
  }
`
const SubCategoryItems = styled.li`
  margin-bottom: 1rem;
  text-transform: capitalize;
`
const SubCategoryItem = styled.a`
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: normal;
  font-style: normal;
  font-stretch: normal;
  line-height: 1.43;
  letter-spacing: normal;
  color: #1557bf;
  text-transform: capitalize;
`

const customLink = (component, url, name, id) => {
  if (url && url.trim()) {
    url = url.replace(/^\/?category\//, '')
    const nameToTrack = name ? encodeURIComponent(name) : ''
    return (
      <Link
        href={encodeURI(
          `/categoryDetails?slug=${encodeURIComponent(
            url
          )}&exclude_ga_track_name=${nameToTrack}&exclude_ga_track_id=${id}`
        )}
        as={`/category/${url}`}
        passHref
        legacyBehavior
      >
        {component}
      </Link>
    )
  } else {
    return component
  }
}

class CategoryContainerListWrapper extends React.Component {
  constructor(props) {
    super(props)

    this.onClick = this.onClick.bind(this)
  }

  onClick(idx) {
    this.setState({ selectedMenuIdx: idx })
  }

  handleLinkClick = (name, level) => {
    this.props.track({
      [GEV.AMPLITUDE_EVENT_NAME]: AMP_EVENT_NAME.CATEGORY_CLICKED,
      [GEV.AMPLITUDE_CATEGORY_NAME]: name,
      [GEV.AMPLITUDE_CATEGORY_LEVEL]: level,
      [GEV.AMPLITUDE_CTA_LOCATION]: {
        pageName: getPageTypeByUrl(),
        pageMeta: getCtaLocationMeta() || [],
      },
    })

    const { checkoutAddress } = this.props
    trackCategoryClickedEvent({
      CATEGORY_NAME: name.toLowerCase(),
      CATEGORY_LEVEL: level,
      STORE_ID_DBP: checkoutAddress?.storeId?.toString() ?? '',
      STORE_ID_SAP: checkoutAddress?.clientId?.toString() ?? '',
    })
  }

  render() {
    const categoryCollection = this.props.data && this.props.data.category
    const selectedMenuIdx = this.state && this.state.selectedMenuIdx
    if (!categoryCollection || !categoryCollection.length) {
      return null
    }
    return (
      <CategoryContainer data-testid="categoryContainer">
        {categoryCollection.map((el, index) => {
          const { menu, url, name, image, id } = el
          return (
            <React.Fragment key={'category-' + index}>
              <StyledCategory
                onClick={() => this.onClick(index)}
                selected={selectedMenuIdx === index}
                order={index}
              >
                <ImageHeading>
                  {image ? (
                    <NoImage data-testid="category-img">
                      <ProgressiveImage
                        src={
                          typeof image === 'string' && image.replace(/XL/g, 'L')
                        }
                        originalSrc={image}
                        alt={name}
                        objectFitContain={true}
                        unoptimized={true}
                      />
                    </NoImage>
                  ) : (
                    <NoImage data-testid="no-img">
                      <NoImg />
                    </NoImage>
                  )}
                  <Heading>
                    <CategoryHeading
                      as="p"
                      size="small"
                      weight="bold"
                      color="#333333"
                    >
                      {name.toLowerCase()}
                    </CategoryHeading>
                    {customLink(
                      <CategorySubHeading
                        data-testid="viewAllItem"
                        onClick={() => this.handleLinkClick(name, 1)}
                      >
                        View all
                      </CategorySubHeading>,
                      url,
                      name,
                      id
                    )}
                  </Heading>
                </ImageHeading>
                <SubCategory data-testid="subCategory">
                  {menu &&
                    menu.map(({ name, url }) => (
                      <SubCategoryItems key={`subcategory-${index}${url}`}>
                        {customLink(
                          <SubCategoryItem
                            data-testid="sub-category-item"
                            onClick={() => this.handleLinkClick(name, 2)}
                          >
                            {name.toLowerCase()}
                          </SubCategoryItem>,
                          url,
                          name,
                          id
                        )}
                      </SubCategoryItems>
                    ))}
                </SubCategory>
              </StyledCategory>
              <CategoriesDetails
                menu={el}
                selected={selectedMenuIdx === index}
                index={index}
                checkoutAddress={this.props.checkoutAddress}
              />
            </React.Fragment>
          )
        })}
      </CategoryContainer>
    )
  }
}

const CategoryContainerList = props => (
  <CheckoutAddressConsumer>
    {({ checkoutAddress }) => (
      <EventTrackingConsumer>
        {({ track }) => (
          <CategoryContainerListWrapper
            {...props}
            track={track}
            checkoutAddress={checkoutAddress}
          />
        )}
      </EventTrackingConsumer>
    )}
  </CheckoutAddressConsumer>
)

CategoryContainerList.defaultProps = {
  data: [],
}

export default CategoryContainerList
